import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { rejectReasonSchema, type RejectReasonFormData } from '@/lib/constants/draft'

interface RejectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string) => void
  isPending: boolean
}

export function RejectDialog({ open, onOpenChange, onConfirm, isPending }: RejectDialogProps) {
  const form = useForm<RejectReasonFormData>({
    resolver: zodResolver(rejectReasonSchema),
    defaultValues: { reason: '' },
  })

  const handleSubmit = form.handleSubmit((data) => {
    onConfirm(data.reason)
  })

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) form.reset()
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Draft</DialogTitle>
          <DialogDescription>
            Provide a reason for rejection. The author will see this when reviewing the draft.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              {...form.register('reason')}
              placeholder="Explain why this draft needs revision..."
              rows={3}
              className="mt-2"
              autoFocus
            />
            {form.formState.errors.reason && (
              <p className="text-sm text-destructive mt-1">
                {form.formState.errors.reason.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={isPending || !form.formState.isValid}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reject Draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
